<template>
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Gestión de Roles</h4>
            <div class="d-flex justify-content-between align-items-center">
              <p class="card-category">Lista de roles disponibles en el sistema</p>
              <router-link 
                v-if="hasPermission('role_create')" 
                to="/roles/create" 
                class="btn btn-primary btn-round"
              >
                <i class="fa fa-plus"></i> Nuevo Rol
              </router-link>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
            </div>
            
            <div v-else>
              <div class="table-responsive">
                <table class="table">
                  <thead class="text-primary">
                    <tr>
                      <th>Nombre</th>
                      <th>Permisos</th>
                      <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="role in roles" :key="role.id">
                    <td>{{ role.name }}</td>
                    <td>
                      <span 
                        v-for="permission in role.permissions" 
                        :key="permission.id" 
                        class="badge bg-info me-1"
                      >
                        {{ permission.name }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <router-link 
                          v-if="hasPermission('role_update') && canEditRole(role)" 
                          :to="`/roles/${role.id}/edit`" 
                          class="btn btn-sm btn-warning"
                        >
                          <i class="fa fa-edit"></i>
                        </router-link>
                        <button 
                          v-if="hasPermission('role_delete') && canDeleteRole(role)" 
                          @click="confirmDelete(role)"
                          class="btn btn-sm btn-danger"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div v-if="roles.length === 0" class="text-center">
              <p>No hay roles registrados</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';

export default {
  setup() {
    const roles = ref([]);
    const loading = ref(true);
    const authStore = useAuthStore();
    //const swal = useSwal();
    
    const fetchRoles = async () => {
      loading.value = true;
      try {
        const response = await axios.get('/roles');
        roles.value = response.data.roles;
      } catch (error) {
        console.error('Error al cargar roles:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los roles'
        });
      } finally {
        loading.value = false;
      }
    };
    
    const confirmDelete = (role) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar el rol ${role.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteRole(role.id);
        }
      });
    };
    
    const deleteRole = async (id) => {
      try {
        await axios.get(`/roles/${id}/delete`);
        Swal.fire({
          icon: 'success',
          title: 'Rol eliminado',
          text: 'El rol ha sido eliminado correctamente'
        });
        await fetchRoles();
      } catch (error) {
        console.error('Error al eliminar rol:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar el rol'
        });
      }
    };
    
    const hasPermission = (permission) => {
      return authStore.hasPermission(permission);
    };
    
    const canEditRole = (role) => {
      // Solo el admin puede editar el rol admin
      if (role.name === 'admin') {
        return authStore.hasRole('admin');
      }
      return true;
    };
    
    const canDeleteRole = (role) => {
      // No permitir eliminar roles predefinidos
      return !['admin', 'consultas', 'ventanilla', 'area', 'unidad', 'direccion'].includes(role.name);
    };
    
    onMounted(() => {
      fetchRoles();
    });
    
    return {
      roles,
      loading,
      confirmDelete,
      hasPermission,
      canEditRole,
      canDeleteRole
    };
  }
};
</script>